import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useViewerRole from "@/lib/rights/use-viewer-role";

/**
 * BAB-2943. A merchant can treat a return from the "not treated by the
 * logistician" section, which refunds the customer without the warehouse ever
 * having inspected the parcel. Historically that merchant treatment also
 * closed the logistician track (see
 * `getLineItemNotTreatedByLogisticianQuantity`), so the logistician could no
 * longer record what he physically received.
 *
 * Shops that opt in via `workflowLogistician.canValidateAfterMerchantValidation`
 * let the logistician inspect such returns anyway.
 *
 * Deliberately restricted to the logistician role: reopening the section for
 * the merchant would put already-refunded units back into "to be treated by
 * the merchant", and the accept flow only guards against `quantity_bought` —
 * a second refund would go through on any partially-returned line. The backend
 * additionally refuses to enable this setting while
 * `preferences.autoTreateOnLogisticianValidate` is on, which would turn the
 * late logistician validation into that same merchant accept.
 */
export function useCanLogisticianRevalidateAfterMerchant(): boolean {
  const { data: shop } = useCurrentShopSuspense();
  const isLogistician = useViewerRole() === "logistician";

  return (
    isLogistician &&
    shop.workflowLogistician.activated &&
    shop.workflowLogistician.canValidateAfterMerchantValidation === true
  );
}
